import * as React from "react"
import trans from "trans"
import { connect, Provider } from 'react-redux'
import store from "store";
import { AxiosResponse } from "axios"
import FileInputComponent from 'react-file-input-previews-base64'

import {CustomPalette, CustomPalettesFormType, Palette, SettingsFormType} from "app/types/model-types";
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE_KB, MAX_UPLOAD_FILE_PX, stringToHash } from "shape-edit/forms/helpers";
import toastFactory, {ResponseErrorType} from "app/helpers/toast_factory";
import {createToast} from "actions";
import EditPaletteModal from "./edit-palette-modal"

import {
    updateInputValue as change,
    initForm
} from "app/reducers/form";

import {
    SAVED_OK,
    GENERAL_ERROR,
    UPLOAD_IMAGE_FILE,
    GET_CUSTOM_PALETTES
} from "template-edit/reducers";

import {
    projectId,
    saveCustomPaletteId,
    saveCustomPalettes,
    saveDefaultPaletteId,
    getPaletteFromImageString,
    getCustomPalettes,
} from "app/helpers/api";

import {
    css,
    GridCell,
    Grid,
    Button,
    Theme,
    Icon,
    InputGroup,
    Section,
    modal,
} from "@bdfx/mergado-ui-kit"
import { selectFormValues } from "app/reducers/form";

interface Props {
    palettes: Palette[]
    createToast: typeof createToast
    settingsForm: SettingsFormType
    customPalettesForm: CustomPalettesFormType
    change: typeof change
    initForm: typeof initForm
}

interface State {
    fileName: string
    imageString: string
    extractedColors: string[]
    isExtracting: boolean
}

class Palettes extends React.Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            fileName: null,
            imageString: null,
            extractedColors: null,
            isExtracting: false,
        }
    }

    updatePalettes = () => {
        getCustomPalettes()
            .then((response: AxiosResponse<CustomPalettesFormType>) => {
                this.props.initForm('customPalettesForm', response.data)
            })
            .catch(error => {
                this.props.createToast(toastFactory(GET_CUSTOM_PALETTES.ERROR, error))
            })
    }

    handleSaveCustomPalettesForm = (customPalettesForm: CustomPalettesFormType) => {
        if (customPalettesForm && customPalettesForm.palettes && Array.isArray(customPalettesForm.palettes)) {
            saveCustomPalettes(customPalettesForm)
                .then((response: AxiosResponse<CustomPalettesFormType>) => {
                    this.props.initForm('customPalettesForm', response.data)
                    this.props.createToast(toastFactory(SAVED_OK))
                })
                .catch(error => {
                    console.error('cannot save custom palettes', error)
                })
        }
    }

    handleSaveSelectedPaletteId = (selectedPaletteId: number) => {
        saveDefaultPaletteId(selectedPaletteId)
            .then((response: AxiosResponse<any>) => {
                this.props.createToast(toastFactory(SAVED_OK))
            })
            .catch(error => {
                console.error('cannot save default palette', error)
            })
    }

    handleSaveSelectedCustomPaletteId = (selectedPaletteIdx: number) => {
        const { customPalettesForm } = this.props
        const newCustomPaletteId = customPalettesForm.palettes[selectedPaletteIdx]?.id
        if (newCustomPaletteId) {
            saveCustomPaletteId(newCustomPaletteId)
                .then((response: AxiosResponse<any>) => {
                    this.props.change('settingsForm', 'custom_palette_id', newCustomPaletteId)
                })
                .catch(error => {
                    console.error('cannot set default custom palette id', error)
                })
        }
    }

    renderPaletteColors = (palette: Palette | CustomPalette) => {
        return palette ? palette.colors.map((color: string, colorIdx: number) => {
            return <span key={`palette_${palette.id}_${color}_${colorIdx}`}>
                <div className={`color-sample`} style={{ background: color }}></div>
            </span>
        }) : undefined
    }

    handleDeleteCustomPaletteIdx = (customPalettesForm: CustomPalettesFormType, paletteIdx: number) => {
        const updatedPalettes = customPalettesForm.palettes.filter((palette: CustomPalette, index: number) => index !== paletteIdx)
        const updatedForm = {
            ...customPalettesForm,
            palettes: updatedPalettes,
        }

        this.props.change("customPalettesForm", 'palettes', updatedPalettes)

        if (this.props.settingsForm.custom_palette_id === customPalettesForm.palettes[paletteIdx].id) {
            this.props.change("settingsForm", 'custom_palette_id', null)
        }

        this.handleSaveCustomPalettesForm(updatedForm)
    }

    showEditPaletteColorsEditModal = (customPalettesForm: CustomPalettesFormType, paletteIdx: number) => {
        const initialPalette = customPalettesForm.palettes[paletteIdx]
        modal({
            overlayClassName: 'edit-palette-colors-modal',
            title: initialPalette.name,
            closeOnEscape: true,
            closeOnClickOutside: true,
            onClose: () => this.handleSaveCustomPalettesForm(this.props.customPalettesForm),
            message: (close) => <Provider store={store}>
                <EditPaletteModal
                    close={close}
                    paletteIdx={paletteIdx}
                    initialPalette={initialPalette}
                    handleSaveCustomPalettesForm={this.handleSaveCustomPalettesForm}
                />
            </Provider>
        })
    }

    handleAddToCustomPalettes = (extractedColors: string[], fileName: string, customSetFileId: number) => {
        const {customPalettesForm} = this.props

        const newCustomPalette: CustomPalette = {
            id: null,
            project_id: projectId,
            name: fileName,
            custom_set_file_id: customSetFileId,
            colors: extractedColors,
        }

        let newCustomPalettes = []
        newCustomPalettes.push(newCustomPalette)

        if (Array.isArray(customPalettesForm.palettes)) {
            newCustomPalettes.push(...customPalettesForm.palettes)
        }

        const updatedCustomPalettesForm = {
            ...customPalettesForm,
            palettes: newCustomPalettes,
        }

        this.props.change('customPalettesForm', 'palettes', newCustomPalettes)
        this.handleSaveCustomPalettesForm(updatedCustomPalettesForm)
    }

    render() {
        const { palettes, customPalettesForm, settingsForm } = this.props
        const { imageString, fileName, extractedColors, isExtracting } = this.state

        const selectedPalette = palettes ? palettes.find((p) => p.id === settingsForm.palette_id) : null
        const selectedCustomPalette = settingsForm.custom_palette_id && customPalettesForm && customPalettesForm.palettes && Array.isArray(customPalettesForm.palettes) ? customPalettesForm.palettes.find((p) => p.id === settingsForm.custom_palette_id) : null

        const customPalettesHash = stringToHash(JSON.stringify(customPalettesForm.palettes))

        const mappedPaletteItems = palettes ? palettes.map((palette: Palette) => {
            return {
                value: palette.id,
                label: <div key={`g_${palette.id}`}>
                    {palette.colors.map((color: string, colorIdx: number) => {
                        return <span key={`i_${color}_${palette.id}_${colorIdx}`}>
                            <div className={`color-sample`} style={{ background: color }}></div>
                        </span>
                    })}
                </div>
            }
        }) : null

        const mappedCustomPaletteItems = customPalettesForm && customPalettesForm.palettes && Array.isArray(customPalettesForm.palettes) ?
            customPalettesForm.palettes.map((customPalette: CustomPalette, paletteIdx: number) => {
                return {
                    value: paletteIdx,
                    label: <StyledPalettesGrid cols={'104px 104px auto 104px'} key={`g_${customPalette.id}`}>
                        <GridCell>{this.renderPaletteColors(customPalette)}</GridCell>
                        <GridCell className={'palette-image'}>{customPalette.image_url && <img src={customPalette.image_url} />}</GridCell>
                        <GridCell className={'palette-name'}>{customPalette.name}</GridCell>
                        <GridCell>
                            <Button
                                type={'void'}
                                color="nocolor"
                                title={trans('delete')}
                                size="small"
                                icon={<Icon type={'trash'} color={Theme.red} size={24} />}
                                onClick={() => this.handleDeleteCustomPaletteIdx(customPalettesForm, paletteIdx)}
                            />
                            <Button
                                type={'void'}
                                color="nocolor"
                                title={trans('edit')}
                                size="small"
                                icon={<Icon type={'edit-light'} color={Theme.steelblue} size={24} />}
                                onClick={() => this.showEditPaletteColorsEditModal(customPalettesForm, paletteIdx)}
                            />
                        </GridCell>
                    </StyledPalettesGrid>
                }
            }) : null

        return (
            <React.Fragment>
                {/* <StyledContent>
                    <StyledSection header={trans('predefined_colors')} suffix={this.renderPaletteColors(selectedPalette)}>
                        {mappedPaletteItems &&
                            <StyledInputGroup
                                orientation="vertical"
                                withoutFilter={true}
                                showInput={true}
                                singleChoice={true}
                                items={mappedPaletteItems}
                                onChange={(value) => this.handleSaveSelectedPaletteId(value)}
                                name="settingsForm.palette_id"
                            />
                        }
                    </StyledSection>
                </StyledContent> */}

                <StyledContent>
                    {mappedCustomPaletteItems &&
                        <StyledInputGroup
                            key={`custom_palettes_${stringToHash(JSON.stringify(customPalettesForm))}`}
                            orientation="vertical"
                            withoutFilter={true}
                            showInput={true}
                            singleChoice={true}
                            items={mappedCustomPaletteItems}
                            onChange={(value) => this.handleSaveSelectedCustomPaletteId(value)}
                            name="customPalettesForm.selected_idx"
                        />
                    }
                </StyledContent>

                <StyledContent style={{ alignSelf: 'center' }}>
                    <Grid cols={'auto auto'} gap={10} style={{justifySelf: 'center', marginBottom: '20px'}}>
                        <GridCell style={{alignSelf: 'center'}} className={isExtracting ? 'muk-helper-loading-line with-line' : ''}>
                            {imageString &&
                            <img src={imageString} width={'auto'} style={{overflow: 'hidden', maxWidth: '100%'}}/>
                            }
                        </GridCell>

                        <GridCell style={{alignSelf: 'center', textAlign: 'center'}}>
                            <div style={{display: 'inline-block'}}>
                                {extractedColors &&
                                extractedColors.map((color: string, idx: number) => {
                                    return <span key={`i_${color}${idx}`}>
                                    <div
                                        className={'color-sample-big'}
                                        style={{background: color}}
                                    >&nbsp;</div>
                                </span>
                                })
                                }
                            </div>

                            {fileName &&
                            <div style={{display: 'block'}}>
                                <h4>{fileName}</h4>
                            </div>
                            }
                        </GridCell>
                    </Grid>


                    <Grid cols={'auto auto'} gap={10} style={{justifySelf: 'center', marginBottom: '20px'}}>
                        <GridCell className={'buttons-cell'}>
                            <FileInputComponent
                                key={'upload_file'}
                                id={`upload_file`}
                                parentStyle={{ marginTop: 0, display: "inline-block" }}
                                labelStyle={{ fontSize: 14 }}
                                labelText={''}
                                imagePreview={false}
                                multiple={false}
                                buttonComponent={
                                    <Button
                                        id={'upload_palette_file'}
                                        type="void"
                                        size={'small'}
                                        color={Theme.blue}
                                        secondary={true}
                                        icon={<Icon type={'upload-light'} text={trans('extract_colors_from_image')}/>}
                                    />
                                }
                                callbackFunction={(file) => {
                                    if (file.size > MAX_FILE_SIZE_KB) {
                                        const error: ResponseErrorType = {
                                            message: 'upload_error_large_file',
                                            response: null
                                        }

                                        store.dispatch(createToast(toastFactory(UPLOAD_IMAGE_FILE.ERROR, error)))
                                    } else if (ALLOWED_FILE_TYPES.indexOf(file.type) === -1) {
                                        const error: ResponseErrorType = {
                                            message: 'upload_error_unsupported_type',
                                            response: null
                                        }

                                        store.dispatch(createToast(toastFactory(UPLOAD_IMAGE_FILE.ERROR, error)))
                                    } else {
                                        this.setState({ isExtracting: true, extractedColors: null })

                                        const img = new Image()
                                        img.src = file.base64
                                        img.onload = () => {
                                            if (img.width * img.height > 4*MAX_UPLOAD_FILE_PX) {
                                                const error: ResponseErrorType = {
                                                    message: 'upload_error_image_too_big',
                                                    response: null
                                                }
                                                store.dispatch(createToast(toastFactory(UPLOAD_IMAGE_FILE.ERROR, error)))
                                            } else {
                                                getPaletteFromImageString(file.base64, img.width, img.height, file.type, file.name)
                                                    .then((response: AxiosResponse<any>) => {
                                                        const extractedColors = response.data.colors
                                                        const customSetFileId = response.data.custom_set_file_id

                                                        this.setState({ extractedColors: extractedColors })
                                                        this.handleAddToCustomPalettes(extractedColors, file.name, customSetFileId)
                                                    })
                                                    .catch(error => {
                                                        console.error('cannot get palette from image', error)
                                                    })
                                                    .finally(() => {
                                                        this.setState({ isExtracting: false })
                                                        this.updatePalettes()
                                                    })
                                            }

                                        }

                                        this.setState({imageString: file.base64, fileName: file.name})
                                    }
                                }}
                                accept="image/*"
                            />
                        </GridCell>

                        {/* <GridCell className={'centered'}>
                            {!isExtracting && extractedColors &&
                            <Button
                                type={'void'}
                                size={'small'}
                                icon={<Icon type={'plus-light'} text={trans('add_to_custom_palettes')} />}
                                onClick={() => this.handleAddToCustomPalettes(extractedColors, fileName)}
                            />
                            }
                        </GridCell> */}
                    </Grid>
                </StyledContent>
            </React.Fragment >
        )
    }
}

const StyledContent = css(GridCell)({
    ' .m-item label, .m-item .m-label-wrapper': {
        width: '100%',
    },
    '@media (min-width: 1025px)': {
        padding: '10px',
        float: 'left',
    },
    paddingBottom: '20px',
    ' .muk-datacell': {
        paddingLeft: '20px',
    },
    ' .clickable': {
        cursor: 'pointer',
    },
    ' .info': {
        fill: '#ccc',
    },
    ' .selected-palette-colors': {
        paddingTop: '10px',
    },
    ' .custom-palette-colors': {
        display: 'inline-block',
        border: '2px solid transparent',
    },
    ' .edited-palette': {
        border: '2px solid '+Theme.yellow,
    },
    ' .centered': {
        alignSelf: 'center',
        textAlign: 'center',
    },
    ' .palette-name': {
        paddingLeft: '20px',
    },
    ' .custom-list': {
        paddingTop: '20px',
    },
    ' .color-sample': {
        width: '25px',
        height: '25px',
        float: 'left',
        border: '2px solid transparent',
    },
    ' .edited-color': {
        border: '2px solid '+Theme.blue,
    },
    ' .color-sample-big': {
        width: '45px',
        height: '45px',
        float: 'left',
        border: '2px solid transparent',
    },
    ' .label-selected': {
        maxHeight: '2em',
        overflow: 'hidden',
    },
    ' .buttons': {
        textAlign: 'right',
        padding: '20px 0',
    },
    ' .react-select-item-option': {
        padding: '5px 10px !important',
        lineHeight: '1 !important',
    },
    ' .react-select-item-option-selected': {
        paddingLeft: '5px',
    },
    ' .react-select-item-option-selected:after': {
        marginTop: '-12px',
    },
    ' .buttons-cell': {
        textAlign: 'center',
        alignSelf: 'center',
    },
})

const StyledPalettesGrid = css(Grid)({
    ' .muk-gridcell': {
        alignSelf: 'center',
    },
    ' .palette-image img': {
        maxWidth: '100px',
        maxHeight: '100px',
    }
})

const StyledInputGroup = css(InputGroup)({
    ' li': {
        display: 'inline-grid',
        gridTemplateColumns: 'auto 1fr',
    },
    ' .muk-radio': {
        alignSelf: 'center',
    },
    ' >label': {
        padding: '0 10px',
    },
    ' .muk-inputgroupv__label': {
        margin: '0',
    },
    ' div.button': {
        width: '40px',
        height: '40px',
        textAlign: 'center',
        padding: '5px',
        borderRadius: '50%',
        margin: '5px',
        display: 'inline-grid',
        '&:hover': {
            background: '#dddddd',
        }
    }
})

const StyledSection = css(Section)({
    ' .m-content': {
        padding: 0
    },
    ' .muk-form-group': {
        padding: 0
    },
})

function mapStateToProps(state) {
    return {
        palettes: state.palettes,
        customPalettesForm: selectFormValues(state, 'customPalettesForm'),
        settingsForm: selectFormValues(state,'settingsForm'),
    }
}

export default connect(mapStateToProps, {
    createToast,
    change,
    initForm,
})(Palettes)
